// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Phone-enrollment step. Runs AFTER identity bind (a bound session is required — the backend
// 409s identity_not_bound otherwise). Phone verification is out-of-band (Twilio Verify) via
// our backend: the client never sends a raw phone number. On success a per-identity phone
// login credential is enrolled and we advance to consent. No Firebase / reCAPTCHA here.

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, AlertCircle, Loader2, Lock, RefreshCw, Smartphone, Pencil } from 'lucide-react'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { Logo } from '@/components/Logo'
import { PhoneNumberInput, type PhoneNumberValue } from '@/components/PhoneNumberInput'
import { cn } from '@/lib/utils'
import { getPhoneHint, startPhone, verifyPhone, ApiError } from '@/lib/api'

// ── Phone-entry mode (SECURITY-RELEVANT) ───────────────────────────────────────
// 'on_file' (default): the OTP goes only to the provider-vetted number already on the record;
// the patient never types a number. This closes the account-takeover surface where an
// attacker enrolls their OWN phone against someone else's identity — enrollment requires
// possession of the *patient's* phone. Onboarding MUST stay 'on_file'.
//
// 'manual': free-text entry (PhoneNumberInput). Reopens the arbitrary-phone-enrollment
// surface, so it is NOT for onboarding. It exists for the future returning-login path
// (post-pilot, no deeplink), which legitimately needs free-text entry with a second factor.
// Built now (flag-gated live code, not commented out) so it is reused, not rewritten — but
// the manual submit-to-backend is a documented stub: the pilot /phone/start is on-file-only.
type PhoneEntryMode = 'on_file' | 'manual'
const PHONE_ENTRY_MODE: PhoneEntryMode = 'on_file'

type Phase = 'loading' | 'phone' | 'code' | 'locked' | 'no-contact'
type CodeState = 'idle' | 'loading' | 'wrong' | 'locked'

const MAX_OTP_ATTEMPTS = 3
const RESEND_SECONDS = 60

// Maps backend phone-flow error codes to i18n keys (otp.* namespace).
function phoneErrorKey(err: unknown): string {
  const code = err instanceof ApiError ? err.code : ''
  switch (code) {
    case 'no_contact_number':
      return 'otp.errNoContact'
    case 'identity_not_bound':
      return 'otp.errNotBound'
    case 'rate_limited':
      return 'otp.errRateLimited'
    case 'code_incorrect':
      return 'otp.errInvalidCode'
    case 'verify_unavailable':
    case 'phone_unavailable':
      return 'otp.errVerifyUnavailable'
    default:
      return 'otp.errGeneric'
  }
}

// ── OTP digit input ──────────────────────────────────────────────────────────

interface OtpInputProps {
  value: string
  onChange: (v: string) => void
  isError: boolean
  isLoading: boolean
  shakeKey: number
}

function OtpInput({ value, onChange, isError, isLoading, shakeKey }: OtpInputProps) {
  const { t } = useTranslation()
  const refs = useRef<Array<HTMLInputElement | null>>([])

  // Focus the first box when the code step appears so the patient can type immediately.
  useEffect(() => {
    refs.current[0]?.focus()
  }, [])

  function handleChange(idx: number, inputVal: string) {
    const digits = inputVal.replace(/\D/g, '')
    // SMS autofill (and multi-char paste) dumps the whole code into one box; distribute it
    // across the boxes from here rather than keeping only the last digit.
    if (digits.length > 1) {
      const chars = Array.from({ length: 6 }, (_, i) => value[i] ?? '')
      for (let i = 0; i < digits.length && idx + i < 6; i++) chars[idx + i] = digits[i]!
      onChange(chars.join(''))
      refs.current[Math.min(idx + digits.length, 5)]?.focus()
      return
    }
    const digit = digits.slice(-1)
    const chars = Array.from({ length: 6 }, (_, i) => value[i] ?? '')
    chars[idx] = digit
    onChange(chars.join(''))
    if (digit && idx < 5) refs.current[idx + 1]?.focus()
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && idx > 0) refs.current[idx - 1]?.focus()
    if (e.key === 'ArrowRight' && idx < 5) refs.current[idx + 1]?.focus()
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    onChange(pasted)
    const nextFocus = Math.min(pasted.length, 5)
    refs.current[nextFocus]?.focus()
  }

  return (
    <div
      key={shakeKey}
      role="group"
      aria-label={t('otp.codeGroupLabel')}
      className={cn('flex gap-2.5 justify-center', shakeKey > 0 && 'animate-shake')}
    >
      {Array.from({ length: 6 }).map((_, idx) => (
        <input
          key={idx}
          ref={el => { refs.current[idx] = el }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          aria-label={t('otp.digitLabel', { n: idx + 1 })}
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          value={value[idx] ?? ''}
          onChange={e => handleChange(idx, e.target.value)}
          onKeyDown={e => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          disabled={isLoading}
          className={cn(
            'w-11 h-14 text-center text-xl font-bold rounded-xl border bg-secondary dark:bg-input-bg text-foreground outline-none transition-colors',
            'focus:ring-1',
            isError
              ? 'border-destructive focus:border-destructive focus:ring-destructive/30'
              : value[idx]
                ? 'border-primary/60 focus:border-primary focus:ring-primary/30'
                : 'border-border focus:border-primary focus:ring-primary/30',
            isLoading && 'opacity-50 cursor-not-allowed',
          )}
        />
      ))}
    </div>
  )
}

// ── Resend control ───────────────────────────────────────────────────────────

function ResendControl({ onResend }: { onResend: () => void }) {
  const { t } = useTranslation()
  const [seconds, setSeconds] = useState(RESEND_SECONDS)

  useEffect(() => {
    const id = setInterval(() => setSeconds(s => Math.max(0, s - 1)), 1000)
    return () => clearInterval(id)
  }, [])

  function handleResend() {
    setSeconds(RESEND_SECONDS)
    onResend()
  }

  return (
    <div className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
      <span>{t('otp.didntReceive')}</span>
      {seconds > 0 ? (
        <span className="text-muted-foreground/60">{t('otp.resendIn', { seconds })}</span>
      ) : (
        <button
          type="button"
          onClick={handleResend}
          className="flex items-center gap-1 font-semibold text-primary hover:opacity-80 transition-opacity"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('otp.resendCode')}
        </button>
      )}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PhoneAuthScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const mode = PHONE_ENTRY_MODE

  const [phase, setPhase] = useState<Phase>(mode === 'on_file' ? 'loading' : 'phone')
  const [hint, setHint] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState('')
  const [sending, setSending] = useState(false)
  const [flowError, setFlowError] = useState<string | null>(null)

  // Manual-mode entry (flag-gated; unused while mode === 'on_file').
  const [manual, setManual] = useState<PhoneNumberValue>({ e164: '', isValid: false })

  const [otp, setOtp] = useState('')
  const [codeState, setCodeState] = useState<CodeState>('idle')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)
  const [resendKey, setResendKey] = useState(0)
  const [otpAttempt, setOtpAttempt] = useState(0)

  // On mount (on_file), resolve the masked on-file number(s) to show before sending.
  useEffect(() => {
    if (mode !== 'on_file') return
    let cancelled = false
    getPhoneHint()
      .then(res => {
        if (cancelled) return
        const first = res.phoneHints[0]
        if (!first) {
          setPhase('no-contact')
          return
        }
        setHint(first)
        setPhase('phone')
      })
      .catch(err => {
        if (cancelled) return
        if (err instanceof ApiError && err.code === 'no_contact_number') {
          setPhase('no-contact')
          return
        }
        // Transient hint failure — let the patient still tap Send (server resolves the number).
        setFlowError(phoneErrorKey(err))
        setPhase('phone')
      })
    return () => {
      cancelled = true
    }
  }, [mode])

  async function sendCode(): Promise<void> {
    setSending(true)
    setFlowError(null)
    try {
      const res = await startPhone()
      setSentTo(res.sentTo)
      setPhase('code')
      setOtp('')
      setOtpAttempt(0)
      setCodeState('idle')
      setCodeError(null)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'no_contact_number') {
        setPhase('no-contact')
        return
      }
      setFlowError(phoneErrorKey(err))
    } finally {
      setSending(false)
    }
  }

  function handleSendCode() {
    if (mode === 'manual') {
      // STUB (SECURITY): the pilot backend /phone/start is on-file-only and does not accept a
      // client-supplied number, so there is no endpoint to submit `manual.e164` to yet.
      // TODO(returning-login): call a manual-capable /phone/start variant with manual.e164
      // once the returning-login path lands it (design §6). Until then, manual submit is inert.
      if (!manual.isValid) {
        setFlowError('phoneInput.invalid')
        return
      }
      setFlowError('otp.manualUnavailable')
      return
    }
    void sendCode()
  }

  function handleConfirm() {
    if (codeState === 'locked' || codeState === 'loading') return
    if (otp.replace(/\s/g, '').length < 6) {
      setShakeKey(k => k + 1)
      return
    }
    setCodeState('loading')
    setCodeError(null)
    verifyPhone(otp)
      .then(() => {
        navigate('/consent')
      })
      .catch((err: unknown) => {
        // Only a rejected code counts against the attempt cap; transient failures (rate limit,
        // provider down) surface a neutral error without burning an attempt or locking out.
        const isWrongCode = err instanceof ApiError && err.code === 'code_incorrect'
        if (!isWrongCode) {
          setCodeState('idle')
          setFlowError(phoneErrorKey(err))
          return
        }
        const next = otpAttempt + 1
        setCodeError(phoneErrorKey(err))
        if (next >= MAX_OTP_ATTEMPTS) {
          setCodeState('locked')
        } else {
          setCodeState('wrong')
          setShakeKey(k => k + 1)
          setOtpAttempt(next)
        }
      })
  }

  function handleOtpChange(v: string) {
    setOtp(v)
    if (codeState === 'wrong') setCodeState('idle')
  }

  function handleResend() {
    setResendKey(k => k + 1)
    setOtp('')
    setCodeState('idle')
    setCodeError(null)
    setOtpAttempt(0)
    void sendCode()
  }

  const isWrong = codeState === 'wrong'
  const isLocked = codeState === 'locked'
  const isLoading = codeState === 'loading'
  const isError = isWrong || (otp.length > 0 && otp.length < 6 && codeState !== 'idle')

  const flowErrorBanner = flowError && (
    <div role="alert" className="flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20">
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
      <p className="text-sm text-destructive leading-snug">{t(flowError)}</p>
    </div>
  )

  const loadingBody = (
    <div className="flex flex-col items-center justify-center gap-4 py-8" aria-live="polite">
      <Loader2 className="h-6 w-6 text-primary animate-spin" />
      <p className="text-sm text-muted-foreground">{t('otp.checkingNumber')}</p>
    </div>
  )

  const noContactBody = (
    <div role="alert" className="flex flex-col gap-4">
      <div className="flex items-start gap-3 px-4 py-4 rounded-xl bg-destructive/8 border border-destructive/20">
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-destructive leading-snug">{t('otp.noContactTitle')}</p>
          <p className="text-xs text-destructive/80 mt-1 leading-relaxed">{t('otp.noContactBody')}</p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t('common.contactProvider')}</p>
        </div>
      </div>
    </div>
  )

  const phoneForm = (
    <div className="flex flex-col gap-5">
      {flowErrorBanner}

      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-primary/6 border border-primary/20">
        <Smartphone className="h-4 w-4 shrink-0 text-primary mt-0.5" />
        <p className="text-sm text-foreground leading-snug">
          {mode === 'on_file'
            ? hint
              ? t('otp.sendToMasked', { masked: hint })
              : t('otp.onFileNotice')
            : t('otp.phoneNotice')}
        </p>
      </div>

      {mode === 'manual' && (
        <PhoneNumberInput
          onChange={setManual}
          disabled={sending}
          autoFocus
          error={flowError === 'phoneInput.invalid'}
        />
      )}

      <button
        onClick={handleSendCode}
        disabled={sending}
        className={cn(
          'w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20',
          sending ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80',
        )}
      >
        {sending ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t('otp.sending')}</>
        ) : (
          t('otp.sendCode')
        )}
      </button>
    </div>
  )

  const lockedBanner = (
    <div className="flex flex-col gap-4">
      <div role="alert" className="flex items-start gap-3 px-4 py-4 rounded-xl bg-destructive/8 border border-destructive/20">
        <Lock className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-destructive leading-snug">{t('otp.lockedTitle')}</p>
          <p className="text-xs text-destructive/80 mt-1 leading-relaxed">{t('otp.lockedBody')}</p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t('common.contactProvider')}</p>
        </div>
      </div>
    </div>
  )

  const codeForm = (
    <div className="flex flex-col gap-5" onKeyDown={e => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') handleConfirm() }}>
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-primary/6 border border-primary/20">
        <ShieldCheck className="h-4 w-4 shrink-0 text-primary mt-0.5" />
        <p className="text-sm text-foreground leading-snug">
          {t('otp.codeSentTo', { phone: sentTo })}
        </p>
      </div>

      {flowErrorBanner}

      {isWrong && (
        <div role="alert" className="flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20 animate-in fade-in slide-in-from-top-2 duration-200">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-destructive leading-snug">{t('otp.wrongTitle')}</p>
            <p className="text-xs text-destructive/80 mt-0.5">
              {t(codeError ?? 'otp.wrongBody')}
              {' · '}
              {t('otp.attemptCount', { current: otpAttempt, max: MAX_OTP_ATTEMPTS })}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <OtpInput
          value={otp}
          onChange={handleOtpChange}
          isError={isError}
          isLoading={isLoading}
          shakeKey={shakeKey}
        />
        {otp.length > 0 && otp.length < 6 && (
          <p className="text-xs text-destructive text-center flex items-center justify-center gap-1">
            <AlertCircle className="h-3 w-3 shrink-0" /> {t('otp.allDigitsRequired')}
          </p>
        )}
      </div>

      <button
        onClick={handleConfirm}
        disabled={isLoading}
        className={cn(
          'w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-opacity bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20',
          isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80',
        )}
      >
        {isLoading ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> {t('otp.verifying')}</>
        ) : (
          t('otp.confirmButton')
        )}
      </button>

      <ResendControl key={resendKey} onResend={handleResend} />

      {mode === 'manual' && (
        <button
          type="button"
          onClick={() => { setPhase('phone'); setOtp(''); setCodeState('idle'); setCodeError(null); setOtpAttempt(0) }}
          className="flex items-center justify-center gap-1.5 text-center text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
          {t('otp.changeNumber')}
        </button>
      )}
    </div>
  )

  const body =
    phase === 'loading'
      ? loadingBody
      : phase === 'no-contact'
        ? noContactBody
        : phase === 'phone'
          ? phoneForm
          : isLocked
            ? lockedBanner
            : codeForm

  return (
    <OnboardingLayout>
      {/* ── Mobile ── */}
      <div className="flex flex-col flex-1 md:hidden">
        <div className="flex-shrink-0 bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9] px-6 py-8 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#02C7C3]/20 blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative z-10 mb-5" style={{ filter: 'brightness(0) invert(1)' }}>
            <Logo variant="mark" className="h-8 w-auto" aria-hidden />
          </div>
          <div className="relative z-10 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#02C7C3]/25 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-5 w-5 text-[#02C7C3]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white leading-tight">{t('otp.screenTitle')}</h1>
              <p className="text-white/60 text-xs mt-0.5">{t('otp.screenSubtitle')}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-6 pb-6">
          {body}
          <div className="mt-8 pt-5 border-t border-border flex items-center justify-center gap-2">
            <Lock className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('common.codeExpiresNote')}</span>
          </div>
        </div>
      </div>

      {/* ── Desktop ── */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]" />
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full bg-[#02C7C3]/20 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-[280px] h-[280px] rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative z-10 h-full flex flex-col justify-between px-12 py-14 lg:px-16">
            <div style={{ filter: 'brightness(0) invert(1)' }}>
              <Logo variant="wordmark" className="h-9 w-auto" aria-hidden />
            </div>
            <div>
              <p className="text-[#02C7C3] text-xs font-semibold uppercase tracking-widest mb-4">{t('otp.tagline')}</p>
              <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                {t('otp.headline')}
              </h1>
              <p className="text-white/65 text-base leading-relaxed max-w-sm">
                {t('otp.subheadline')}
              </p>
            </div>
            <div />
          </div>
        </div>

        <div className="w-[460px] xl:w-[520px] bg-secondary dark:bg-background flex items-center justify-center px-10 py-12 overflow-y-auto relative">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full bg-primary/4 blur-3xl" />
          </div>
          <div className="relative z-10 w-full max-w-sm">
            <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-xl shadow-black/10">
              <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
              <div className="p-8">
                <div className="flex flex-col items-center mb-7">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#006EF9] to-[#02C7C3] flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                    <Smartphone className="h-7 w-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground">{t('otp.screenTitle')}</h2>
                  <p className="text-sm text-muted-foreground mt-1 text-center">{t('otp.formSubtitle')}</p>
                </div>

                {body}

                <div className="mt-6 pt-5 border-t border-border flex items-center justify-center gap-2">
                  <Lock className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{t('common.codeExpiresNote')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}
